import type { DateRange } from "@/shared/components/date-picker";
import {
	DATE_ONLY_FORMAT,
	DateRangePicker,
} from "@/shared/components/date-picker";
import { MesfModal } from "@dexteel/mesf-core";
import { Button, DialogContentText, Typography } from "@mui/material";
import { useState } from "react";
import {
	type BillPeriod,
	findOverlappingPeriod,
	formatBillingDate,
	getBilledDays,
} from "../../../shared/utils/billPeriods";
import { toUtcDay } from "../../../shared/utils/date";

interface NewGasBillDialogProps {
	open: boolean;
	periods: BillPeriod[];
	isSaving: boolean;
	onClose: () => void;
	onCreate: (period: {
		ServicePeriodStart: Date;
		ServicePeriodEnd: Date;
	}) => Promise<boolean>;
}

export const NewGasBillDialog = ({
	open,
	periods,
	isSaving,
	onClose,
	onCreate,
}: NewGasBillDialogProps) => {
	const [range, setRange] = useState<DateRange>({ start: null, end: null });

	const billedDays = getBilledDays(periods);
	const overlapping = findOverlappingPeriod(
		periods,
		toUtcDay(range.start),
		toUtcDay(range.end),
		null,
	);

	const canCreate =
		range.start !== null &&
		range.end !== null &&
		overlapping === null &&
		!isSaving;

	const close = () => {
		setRange({ start: null, end: null });
		onClose();
	};

	return (
		<MesfModal open={open} handleClose={close} maxWidth="sm" title="New bill">
			<MesfModal.Content dividers>
				<DialogContentText sx={{ fontSize: "0.85rem", mb: 2 }}>
					Pick the service period of the gas bill. MCF, MMBtu and cost are
					filled in on the bill's row.
				</DialogContentText>
				<DateRangePicker
					value={range}
					onChange={setRange}
					format={DATE_ONLY_FORMAT.LONG}
					labels={{ start: "Start date", end: "End date" }}
					shouldDisableDate={(day) => billedDays.has(day)}
					allowDisabledInside
					clearable
					fullWidth
				/>
				{overlapping !== null && (
					<Typography sx={{ color: "error.main", fontSize: "0.75rem", mt: 1 }}>
						Overlaps the bill of {formatBillingDate(overlapping.start)} →{" "}
						{formatBillingDate(overlapping.end)}
					</Typography>
				)}
			</MesfModal.Content>
			<MesfModal.Actions>
				<Button onClick={close} color="inherit">
					Cancel
				</Button>
				<Button
					variant="contained"
					disabled={!canCreate}
					data-test-id="create-gas-bill"
					onClick={async () => {
						const start = toUtcDay(range.start);
						const end = toUtcDay(range.end);
						if (start === null || end === null) return;

						const created = await onCreate({
							ServicePeriodStart: start,
							ServicePeriodEnd: end,
						});

						if (created) close();
					}}
				>
					Create
				</Button>
			</MesfModal.Actions>
		</MesfModal>
	);
};
